import '../styles/login.css';
import { SmartyLogo } from '@/components/brand/SmartyLogo';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { devLog } from '@/lib/utils';
import { isAdminDomain } from '@/lib/domain';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      if (data.user) {
        toast.success('Sesión iniciada', {
          description: `Bienvenido, ${data.user.email}`,
        });

        const onAdminPath = window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/');
        if (isAdminDomain() || onAdminPath) {
          window.location.href = '/admin';
          return;
        }
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      devLog.error('❌ Error al iniciar sesión:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Error al iniciar sesión. Verifica tus credenciales.';
      setError(errorMessage);
      toast.error('Error al iniciar sesión', {
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__wrap">
        {/* Columna izquierda: branding */}
        <aside className="login-page__branding">
          <div className="login-page__branding-content">
            <img
              src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80"
              alt="Restaurante y servicio gastronómico"
              className="login-page__branding-image"
            />
          </div>
          <p className="login-page__branding-footer">
            © Smarty · POS & Restaurant Management
          </p>
        </aside>

        {/* Columna derecha: formulario */}
        <main className="login-page__form-column">
          <div className="login-page__inner">
            {/* Logo solo en móvil */}
            <div className="login-page__logo">
              <a href="/" className="login-page__logo-link">
                <span className="login-page__logo-icon"><SmartyLogo size={20} color="#fff" /></span>
                Smarty
              </a>
            </div>

            <div className="login-page__card">
              <h1 className="login-page__title">Iniciar sesión</h1>
              <p className="login-page__subtitle">
                Ingresa tu correo y contraseña para acceder.
              </p>

              <form onSubmit={handleLogin} className="login-page__form" data-allow-submit="true">
                {error && (
                  <div className="login-page__error" role="alert">
                    <svg
                      className="login-page__error-icon"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                <div className="login-page__field">
                  <label htmlFor="email" className="login-page__label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="nombre@ejemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="email"
                    className="login-page__input"
                  />
                </div>

                <div className="login-page__field">
                  <label htmlFor="password" className="login-page__label">
                    Contraseña
                  </label>
                  <input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="current-password"
                    className="login-page__input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="login-page__submit"
                >
                  {loading ? (
                    <>
                      <span className="login-page__spinner" aria-hidden />
                      Entrando...
                    </>
                  ) : (
                    'Iniciar sesión'
                  )}
                </button>
              </form>

              <p className="login-page__footer">
                ¿No tienes cuenta? Contacta al administrador para darte de alta.
              </p>
            </div>

            <p className="login-page__copyright">
              © Smarty · POS & Restaurant Management
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
